/**
 * <s-header-search> — desktop header search field with an inline results panel.
 *
 * Typing fetches Shopify predictive search suggestions (JSON) and renders two
 * columns under the field: query suggestions and products. A footer row links to
 * the full search results page for the typed terms.
 *
 * Required child markup:
 *   [data-header-search-input]              — the search <input> (role="combobox").
 *   [data-header-search-panel]              — results panel (hidden until there are terms).
 *   [data-header-search-suggestions-group]  — wrapper shown when suggestions exist.
 *   [data-header-search-suggestions]        — <ul> for suggestion rows.
 *   [data-header-search-products-group]     — wrapper shown when products exist.
 *   [data-header-search-collections-group]  — wrapper shown when collections exist.
 *   [data-header-search-collections]        — <ul> for collection links (third column).
 *   [data-header-search-grid]               — grid; columns are set to the visible groups.
 *   [data-header-search-products]           — <ul> for product rows.
 *   [data-header-search-empty]              — message shown when nothing matches.
 *   [data-header-search-all]                — footer link to the full results page.
 *   [data-header-search-all-label]          — text inside the footer link.
 *
 * Data attributes (written by Liquid):
 *   data-suggest-url  — routes.predictive_search_url
 *   data-search-url   — routes.search_url
 *   data-currency     — presentment currency ISO code for price formatting
 *   data-locale       — storefront locale for price formatting
 *   data-search-for   — translated "Search for …" text with a __TERMS__ token
 *   data-no-results   — translated "No results …" text with a __TERMS__ token
 *
 * Keyboard: Arrow Down / Up move through result links, Escape closes the panel and
 * returns focus to the input. Clicking outside closes the panel.
 */
class SHeaderSearch extends HTMLElement {
  #ac;
  /** @type {AbortController | null} */
  #fetchAc = null;
  #timer = 0;
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {HTMLElement | null} */
  #panel = null;
  /** @type {Intl.NumberFormat | null} */
  #money = null;
  #lastTerms = '';

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#input = this.querySelector('[data-header-search-input]');
    this.#panel = this.querySelector('[data-header-search-panel]');
    if (!this.#input || !this.#panel) return;

    try {
      this.#money = new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      });
    } catch {
      this.#money = null;
    }

    this.#input.addEventListener('input', () => this.#schedule(), opts);
    this.#input.addEventListener(
      'focus',
      () => {
        if (this.#input?.value.trim() && this.#lastTerms) this.#open();
        else if (this.#input?.value.trim()) this.#schedule();
      },
      opts
    );
    this.addEventListener('keydown', (event) => this.#onKeydown(event), opts);

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!(event.target instanceof Node)) return;
        // The scrim lives inside this element, so a click on it counts as "outside".
        const onScrim = event.target instanceof Element && event.target.matches('[data-header-search-scrim]');
        if (onScrim || !this.contains(event.target)) this.#close();
      },
      opts
    );

    this.addEventListener(
      'focusout',
      (event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && this.contains(next)) return;
        if (next) this.#close();
      },
      opts
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#fetchAc?.abort();
    clearTimeout(this.#timer);
  }

  #schedule() {
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#search(), 250);
  }

  async #search() {
    const terms = this.#input?.value.trim() ?? '';
    if (!terms) {
      this.#lastTerms = '';
      this.#close();
      return;
    }

    this.#fetchAc?.abort();
    this.#fetchAc = new AbortController();

    const url = new URL(`${this.dataset.suggestUrl}.json`, window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('resources[type]', 'query,product,collection');
    url.searchParams.set('resources[limit]', '7');
    url.searchParams.set('resources[limit_scope]', 'each');

    try {
      const response = await fetch(url, { signal: this.#fetchAc.signal });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      if (terms !== (this.#input?.value.trim() ?? '')) return;
      const results = data?.resources?.results ?? {};
      this.#render(terms, results.queries ?? [], (results.products ?? []).slice(0, 3), (results.collections ?? []).slice(0, 4));
      this.#lastTerms = terms;
      this.#open();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#render(terms, [], [], []);
      this.#open();
    }
  }

  /**
   * @param {string} terms
   * @param {Array<{ text: string, styled_text?: string, url: string }>} queries
   * @param {Array<{ title: string, url: string, image?: string, featured_image?: { url?: string }, price?: string, compare_at_price_max?: string }>} products
   * @param {Array<{ title: string, url: string }>} [collections]
   */
  #render(terms, queries, products, collections = []) {
    const grid = this.querySelector('[data-header-search-grid]');
    const suggestionsGroup = this.querySelector('[data-header-search-suggestions-group]');
    const suggestionsList = this.querySelector('[data-header-search-suggestions]');
    const productsGroup = this.querySelector('[data-header-search-products-group]');
    const productsList = this.querySelector('[data-header-search-products]');
    const collectionsGroup = this.querySelector('[data-header-search-collections-group]');
    const collectionsList = this.querySelector('[data-header-search-collections]');
    const empty = this.querySelector('[data-header-search-empty]');
    const all = this.querySelector('[data-header-search-all]');
    const allLabel = this.querySelector('[data-header-search-all-label]');

    // Suggestions: soft grey pills that wrap.
    suggestionsList?.replaceChildren(
      ...queries.map((query) =>
        this.#linkItem(
          query.url,
          query.text,
          'inline-flex items-center rounded-full bg-surface-secondary px-3.5 py-2 text-sm font-medium text-on-surface no-underline hover:text-interactive focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
        )
      )
    );

    productsList?.replaceChildren(...products.map((product) => this.#productRow(product)));

    // Collections: a simple list of links in their own column.
    collectionsList?.replaceChildren(
      ...collections.map((collection) =>
        this.#linkItem(
          collection.url,
          collection.title,
          'block rounded-sm py-1.5 text-sm font-medium text-on-surface no-underline hover:text-interactive focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
        )
      )
    );

    suggestionsGroup?.toggleAttribute('hidden', queries.length === 0);
    productsGroup?.toggleAttribute('hidden', products.length === 0);
    collectionsGroup?.toggleAttribute('hidden', collections.length === 0);

    // Only visible columns take space, so the panel never shows an empty gap.
    const columns = [];
    if (queries.length) columns.push('minmax(0,1fr)');
    if (products.length) columns.push('minmax(0,1.3fr)');
    if (collections.length) columns.push('minmax(0,0.9fr)');
    // Desktop reads --search-cols (config/styles.css); below md the groups stack in one column.
    if (grid instanceof HTMLElement) grid.style.setProperty('--search-cols', columns.length ? columns.join(' ') : '1fr');

    if (empty) {
      const nothing = columns.length === 0;
      empty.toggleAttribute('hidden', !nothing);
      empty.textContent = nothing ? (this.dataset.noResults ?? '').replace('__TERMS__', terms) : '';
    }

    if (all instanceof HTMLAnchorElement) {
      const searchUrl = new URL(this.dataset.searchUrl ?? '/', window.location.origin);
      searchUrl.searchParams.set('q', terms);
      searchUrl.searchParams.set('type', 'product');
      searchUrl.searchParams.set('options[unavailable_products]', 'last');
      all.href = searchUrl.pathname + searchUrl.search;
      all.dataset.headerSearchOption = '';
    }
    if (allLabel) allLabel.textContent = (this.dataset.searchFor ?? '').replace('__TERMS__', terms);
  }

  /**
   * @param {string} url
   * @param {string} text
   * @param {string} className
   */
  #linkItem(url, text, className) {
    const li = document.createElement('li');
    li.className = 'm-0';
    const a = document.createElement('a');
    a.href = url;
    a.dataset.headerSearchOption = '';
    a.className = className;
    a.textContent = text;
    li.append(a);
    return li;
  }

  /** @param {{ title: string, url: string, image?: string, featured_image?: { url?: string }, price?: string, compare_at_price_max?: string }} product */
  #productRow(product) {
    const li = document.createElement('li');
    li.className = 'm-0';

    const a = document.createElement('a');
    a.href = product.url;
    a.dataset.headerSearchOption = '';
    a.className =
      'group flex items-center gap-4 rounded-sm text-on-surface no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';

    const imageSrc = product.featured_image?.url || product.image;
    const media = document.createElement('span');
    media.className = 'block size-20 shrink-0 overflow-hidden rounded-sm';
    if (imageSrc) {
      const img = document.createElement('img');
      const src = new URL(imageSrc, window.location.origin);
      src.searchParams.set('width', '160');
      img.src = src.toString();
      img.alt = '';
      img.width = 80;
      img.height = 80;
      img.loading = 'lazy';
      img.className = 'size-full object-contain';
      media.append(img);
    }

    const body = document.createElement('span');
    body.className = 'flex min-w-0 flex-col gap-1';

    const title = document.createElement('span');
    title.className = 'line-clamp-2 text-sm font-semibold leading-snug group-hover:text-interactive';
    title.textContent = product.title;
    body.append(title);

    const price = Number.parseFloat(product.price ?? '');
    const compare = Number.parseFloat(product.compare_at_price_max ?? '');
    if (!Number.isNaN(price)) {
      const prices = document.createElement('span');
      prices.className = 'flex items-baseline gap-2';
      const onSale = !Number.isNaN(compare) && compare > price;
      const current = document.createElement('span');
      current.className = onSale ? 'text-base font-semibold text-sale' : 'text-base font-semibold text-on-surface';
      current.textContent = this.#format(price);
      prices.append(current);
      if (onSale) {
        const was = document.createElement('s');
        was.className = 'text-xs text-on-surface-secondary';
        was.textContent = this.#format(compare);
        prices.append(was);
      }
      body.append(prices);
    }

    a.append(media, body);
    li.append(a);
    return li;
  }

  /** @param {number} amount */
  #format(amount) {
    return this.#money ? this.#money.format(amount) : amount.toFixed(2);
  }

  #open() {
    if (!this.#panel || !this.#input) return;
    this.#panel.hidden = false;
    this.#input.setAttribute('aria-expanded', 'true');
    this.toggleAttribute('data-open', true);
  }

  #close() {
    if (!this.#panel || !this.#input) return;
    this.#panel.hidden = true;
    this.#input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('data-open');
  }

  /** @param {KeyboardEvent} event */
  #onKeydown(event) {
    if (event.key === 'Escape') {
      if (this.#panel && !this.#panel.hidden) {
        event.preventDefault();
        this.#close();
        this.#input?.focus();
      }
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (!this.#panel || this.#panel.hidden) return;

    const options = [...this.querySelectorAll('[data-header-search-option]')].filter(
      (el) => el instanceof HTMLElement && el.offsetParent !== null
    );
    if (options.length === 0) return;
    event.preventDefault();

    const current = options.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    let next;
    if (event.key === 'ArrowDown') next = current + 1;
    else next = current === -1 ? options.length - 1 : current - 1;

    if (next < 0 || next >= options.length) {
      this.#input?.focus();
      return;
    }
    /** @type {HTMLElement} */ (options[next]).focus();
  }
}

if (!customElements.get('s-header-search')) {
  customElements.define('s-header-search', SHeaderSearch);
}
